import React, { Component, useCallback, useEffect, useState } from "react";
import { PorAsinListTable } from "../porAsinList/PorAsinListTable";
import AppLayoutToolbar from "@amzn/awsui-components-react/polaris/app-layout-toolbar";
import { SearchSideBar } from "../searchSideBar/searchSideBar";
import { useSelector } from "react-redux";
import { RootState } from "./rootReducer";
import Tabs from "@amzn/awsui-components-react/polaris/tabs";
import { Notification } from "../notification/notification";
import NoSsr from "./NoSsr";
import "./App.css";
import { AsinValidationTable } from "../validation/asinValidationTable";
import { BaselineTable } from "../dateBaseline/baselineTable";
import {
  addOrUpdateUrlParameter,
  getValueFromUrlParameter,
  TAB_ID_KEY,
} from "../../util/urlUtils";
import { OneClickLaunchTable } from "../oneClickLaunch/oneClickLaunchTable";
import { OclSubmissionTools } from "../oneClickLaunch/oneClickLaunchSubmissionTools";
import { CacheableNotification } from "../notification/cacheableNotification";
import { StamperWizard } from "../stampUtils/StamperWizard";
import { InitializeRum } from "../rum/initializeRum";
import { LoomAgentAssistantWidget } from "../../components/loom-agent-assistant/LoomAgentAssistantWidget";
import { selectIsLoomAgentAssistantEnabled } from "../userInfo/userInfoSlice";
import { AppShell } from "../appShell/AppShell";
import { isV2Enabled } from "./featureFlag";
import { LEFT_NAV_ITEMS } from "../appShell/shellRoutes";

// Paths that only render inside App Shell. Non-flagged users landing
// on one of these get bounced to `/` (Legacy).
const APP_SHELL_TOP_LEVEL_PATHS = new Set<string>(
  LEFT_NAV_ITEMS.map((item) => item.path)
);

function isOnAppShellPath(): boolean {
  if (typeof window === "undefined") return false;
  const firstSegment =
    "/" + window.location.pathname.replace(/^\/+/, "").split("/")[0];
  return APP_SHELL_TOP_LEVEL_PATHS.has(firstSegment);
}

/** Preserve `search` + `hash` when redirecting to Legacy. */
function toLegacyUrl(): string {
  const { search, hash } = window.location;
  return "/" + search + hash;
}

const Content = () => {
  const { isEditing } = useSelector((state: RootState) => state.porAsinList);
  const isLoomAgentAssistantEnabled = useSelector(
    selectIsLoomAgentAssistantEnabled
  );
  const [tab, setTab] = useState("asins");
  const [activeDrawerId, setActiveDrawerId] = useState<string | null>(null);
  const [navigationOpen, setNavigationOpen] = useState(true);
  const tabIdFromUrl = getValueFromUrlParameter(TAB_ID_KEY, "asins");
  useEffect(() => {
    setTab(tabIdFromUrl);
  }, [tabIdFromUrl]);

  // Controlled handlers required by AppLayoutToolbar for correct
  // toggle + responsive panel behavior.
  const handleNavigationChange = useCallback(
    ({ detail }: { detail: { open: boolean } }) => {
      setNavigationOpen(detail.open);
    },
    []
  );

  const handleDrawerChange = useCallback(
    ({ detail }: { detail: { activeDrawerId: string | null } }) => {
      setActiveDrawerId(detail.activeDrawerId);
    },
    []
  );

  //TODO: Implement proper router after refactoring query href logic
  const stamperView = window.location.href.split("/").slice(-1)[0] == "stamper";

  return (
    <div className="awsui" data-testid="app-content-wrapper">
      <InitializeRum />
      <AppLayoutToolbar
        data-testid="app-layout"
        headerSelector={"#mosaic-navigation"}
        footerSelector={"#footer"}
        navigationOpen={navigationOpen}
        onNavigationChange={handleNavigationChange}
        navigation={
          <NoSsr>
            <SearchSideBar />
          </NoSsr>
        }
        navigationHide={isEditing || tab === "launch" || stamperView}
        contentType={"table"}
        toolsHide={true}
        notifications={
          <div data-testid="notifications-container">
            <Notification />
            <CacheableNotification />
          </div>
        }
        stickyNotifications={true}
        navigationWidth={300}
        activeDrawerId={activeDrawerId}
        onDrawerChange={handleDrawerChange}
        drawers={[
          ...(isLoomAgentAssistantEnabled
            ? [
                {
                  id: "agent-assistant",
                  ariaLabels: {
                    drawerName: "Loom Agent Assistant",
                    closeButton: "Close Loom Agent Assistant",
                    triggerButton: "Open Loom Agent Assistant",
                    resizeHandle: "Resize Loom Agent Assistant panel",
                  },
                  trigger: { iconName: "gen-ai" as const },
                  resizable: true,
                  defaultSize: 400,
                  content: (
                    <div
                      data-testid="agent-assistant-drawer"
                      style={{
                        height: "100%",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      <LoomAgentAssistantWidget />
                    </div>
                  ),
                },
              ]
            : []),
          ...(tab === "launch" && !stamperView
            ? [
                {
                  id: "submission-history",
                  ariaLabels: {
                    drawerName: "Submission History",
                    closeButton: "Close submission history",
                    triggerButton: "Open submission history",
                    resizeHandle: "Resize submission history panel",
                  },
                  trigger: { iconName: "status-info" as const },
                  resizable: true,
                  defaultSize: 420,
                  content: (
                    <div
                      data-testid="submission-history-drawer"
                      style={{
                        padding: "20px",
                      }}
                    >
                      <OclSubmissionTools />
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        content={
          <div id={"app-content"} data-testid="app-content">
            {stamperView ? (
              StamperWizard()
            ) : (
              <Tabs
                data-testid="main-tabs"
                activeTabId={tab}
                tabs={[
                  {
                    label: "ASINs",
                    id: "asins",
                    content: <PorAsinListTable />,
                  },
                  {
                    label: "Validation",
                    id: "validation",
                    content: <AsinValidationTable />,
                  },
                  {
                    label: "Baseline",
                    id: "baseline",
                    content: <BaselineTable />,
                  },
                  {
                    label: "Launch",
                    id: "launch",
                    content: <OneClickLaunchTable />,
                  },
                ]}
                onChange={(event) => {
                  const tabId = event.detail.activeTabId;
                  addOrUpdateUrlParameter(TAB_ID_KEY, tabId);
                  setTab(tabId);
                }}
              />
            )}
          </div>
        }
      />
    </div>
  );
};

/**
 * Flagged: render `<AppShell/>` (owns its own routing).
 * Unflagged on a shell path: bounce to `/` preserving filter.
 * Unflagged elsewhere: Legacy `<Content/>`.
 */
class App extends Component {
  render() {
    if (isV2Enabled()) {
      return <AppShell />;
    }
    if (isOnAppShellPath()) {
      window.location.replace(toLegacyUrl());
      return null;
    }
    return <Content />;
  }
}

export default App;
